import { FaroTableFooterContainer } from "@components/common/faro-table/faro-table-footer-container";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { Box } from "@mui/material";
import { DASH } from "@utils/ui-utils";
import ArrowLeftIcon from "@assets/icons/new/arrow-left_18px.svg?react";
import ArrowRightIcon from "@assets/icons/new/arrow-right_18px.svg?react";

interface Props {
  /** Index of the first item in the page */
  from: number;
  /** Index of the last item in the page */
  to: number;
  /** Total row count. If it's still unknown it defaults to -1 */
  rowCount: number;
  /** Current page */
  page: number;
  /** The last page. If it's still unknown it defaults to -1 */
  lastPage: number;
  /** Whether the projects table is in loading state */
  isLoading: boolean;
  /** Callback to handle a change of page */
  handlePageChange: (newPage: number) => void;
}

/**
 * Custom footer component for paginated tables with unknown total counts.
 *
 * This component is intended for scenarios where:
 * - The total number of items is unknown or expensive to calculate at first load
 * - Navigation is based on the presence of a "next" page
 * - Data is loaded progressively as the user navigates to the next page
 *
 * Display logic:
 * - When total count is unknown (`rowCount === -1`):e.g. "1 – 30 of more than 30"
 * - When the total count is known (`rowCount !== -1`): e.g. "1 – 30 of 100"
 *
 */
export function FaroPaginatedTableFooter({
  from,
  to,
  rowCount,
  page,
  lastPage,
  isLoading,
  handlePageChange,
}: Props): JSX.Element {
  return (
    <FaroTableFooterContainer>
      <Box component="var" sx={{ fontWeight: "bold" }}>{`${from} ${DASH} ${to} `}</Box>
      <Box component="span" sx={{ marginX: "4px" }}>
        {rowCount !== -1
          ? <>of <var>{rowCount}</var></>
          : <>of more than <var>{to}</var></>
        }
      </Box>
        <FaroIconButton
          component={ArrowLeftIcon}
          isDisabled={page === 0 || isLoading}
          onClick={() => handlePageChange(page - 1)}
          buttonSize="28px"
          iconSize="20px"
          aria-label="previous page"
        />
      <Box sx={{ marginLeft: "4px" }}>
        <FaroIconButton
          component={ArrowRightIcon}
          isDisabled={(lastPage !== -1 && page >= lastPage) || isLoading}
          onClick={() => handlePageChange(page + 1)}
          buttonSize="28px"
          iconSize="20px"
          aria-label="next page"
        />
      </Box>
    </FaroTableFooterContainer>
  );
}
